import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authApi } from '../utils/api';

interface User {
  id: string;
  email: string;
  userType: string;
  role?: string;
  permissions?: string[];
  tenantId?: string;
  platformUserId?: string;
  requirePasswordReset?: boolean;
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

interface MfaChallenge {
  requiresTwoFactor: true;
  mfaToken: string;
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const accessToken = ref<string | null>(null);
  const refreshToken = ref<string | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const pendingMfaToken = ref<string | null>(null);
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value);

  // Load auth state from localStorage on initialization
  function loadAuthState() {
    const storedToken = localStorage.getItem('admin:accessToken');
    const storedRefreshToken = localStorage.getItem('admin:refreshToken');
    const storedUser = localStorage.getItem('admin:user');

    if (storedToken) {
      accessToken.value = storedToken;
    }
    if (storedRefreshToken) {
      refreshToken.value = storedRefreshToken;
    }
    if (storedUser) {
      try {
        user.value = JSON.parse(storedUser);
      } catch {
        user.value = null;
      }
    }
  }

  // Save auth state to localStorage
  function saveAuthState(tokens: AuthTokens, userData: User) {
    localStorage.setItem('admin:accessToken', tokens.accessToken);
    localStorage.setItem('admin:refreshToken', tokens.refreshToken);
    localStorage.setItem('admin:user', JSON.stringify(userData));

    accessToken.value = tokens.accessToken;
    refreshToken.value = tokens.refreshToken;
    user.value = userData;
  }

  // Clear auth state
  function clearAuthState() {
    localStorage.removeItem('admin:accessToken');
    localStorage.removeItem('admin:refreshToken');
    localStorage.removeItem('admin:user');

    accessToken.value = null;
    refreshToken.value = null;
    user.value = null;
    error.value = null;
  }

  // Login — returns MfaChallenge when 2FA is required, full auth state otherwise
  async function login(
    email: string,
    password: string
  ): Promise<{ requiresTwoFactor?: boolean } & Partial<{ user: User }>> {
    isLoading.value = true;
    error.value = null;

    try {
      const response: (AuthTokens & { user: User }) | MfaChallenge = await authApi.login({
        email,
        password,
      });

      if ('requiresTwoFactor' in response && response.requiresTwoFactor) {
        pendingMfaToken.value = response.mfaToken;
        return { requiresTwoFactor: true };
      }

      const fullResponse = response as AuthTokens & { user: User };
      saveAuthState(
        { accessToken: fullResponse.accessToken, refreshToken: fullResponse.refreshToken },
        fullResponse.user
      );
      return { user: fullResponse.user };
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Login failed. Please try again.';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // Complete login with TOTP code after password step
  async function verifyTotp(totpCode: string) {
    if (!pendingMfaToken.value) throw new Error('No pending MFA challenge');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authApi.verifyTotpLogin(pendingMfaToken.value, totpCode);
      pendingMfaToken.value = null;
      saveAuthState(
        { accessToken: response.accessToken, refreshToken: response.refreshToken },
        response.user
      );
      return response;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Invalid code. Please try again.';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // Logout
  async function logout() {
    try {
      if (accessToken.value) {
        await authApi.logout();
      }
    } catch {
      // Ignore logout errors
    } finally {
      clearAuthState();
    }
  }

  // Refresh token
  async function refreshAccessToken() {
    if (!refreshToken.value) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await authApi.refreshToken(refreshToken.value);
      accessToken.value = response.accessToken;
      localStorage.setItem('admin:accessToken', response.accessToken);
      return response;
    } catch (err) {
      clearAuthState();
      throw err;
    }
  }

  // Initialize auth state on store creation
  loadAuthState();

  return {
    user,
    accessToken,
    refreshToken,
    pendingMfaToken,
    isLoading,
    error,
    isAuthenticated,
    login,
    verifyTotp,
    logout,
    refreshAccessToken,
    clearAuthState,
  };
});
